/*
 * Copyright (C) 2018 - 2023 Entgra (Pvt) Ltd, Inc - All Rights Reserved.
 *
 * Unauthorised copying/redistribution of this file, via any medium is strictly prohibited.
 *
 * Licensed under the Entgra Commercial License, Version 1.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *       https://entgra.io/licenses/entgra-commercial/1.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import React from 'react';
import {
  Card,
  List,
  Divider,
  Row,
  Col,
  Typography,
  Avatar,
  Tag,
  Button,
  Result,
  Space,
} from 'antd';
import axios from 'axios';
import { handleApiError } from '../../../../../../../../services/utils/errorHandler';
import { withConfigContext } from '../../../../../../../../components/ConfigContext';
import { Link } from 'react-router-dom';
import { Icon as LegacyIcon } from '@ant-design/compatible';
import {
  EyeOutlined,
  CheckOutlined,
  ExclamationOutlined,
  DownOutlined,
  UpOutlined,
} from '@ant-design/icons';
import styles from './styles.module.css';
import { withTranslation } from 'react-i18next';

const { Title, Text } = Typography;
const loadLimit = 2;

class DevicePolicyCompliance extends React.Component {
  constructor(props) {
    super(props);
    this.config = this.props.context;
    this.state = {
      complianceData: null,
      effectivePolicy: null,
      loadedGroups: [],
      loadedRoles: [],
      loadedUsers: [],
      expandedOwnershipTypes: false,
    };
  }

  componentDidMount() {
    this.getFeatureCompliance();
    this.getEffectivePolicy();
  }

  getFeatureCompliance = (params = {}) => {
    const config = this.props.context;
    const { deviceIdentifier, deviceType, t } = this.props;
    axios
      .get(
        window.location.origin +
          config.serverConfig.invoker.uri +
          config.serverConfig.invoker.deviceMgt +
          `/devices/${deviceType}/${deviceIdentifier}/compliance-data`,
      )
      .then(res => {
        if (res.status === 200) {
          this.setState({
            complianceData: res.data.data.complianceData,
          });
        }
      })
      .catch(error => {
        handleApiError(error, t('api_retrieveDeviceDetailsError'), t);
      });
  };

  getEffectivePolicy = (params = {}) => {
    const config = this.props.context;
    const { deviceIdentifier, deviceType, t } = this.props;

    axios
      .get(
        window.location.origin +
          config.serverConfig.invoker.uri +
          config.serverConfig.invoker.deviceMgt +
          `/devices/${deviceType}/${deviceIdentifier}/effective-policy`,
      )
      .then(res => {
        if (res.status === 200) {
          let policyData = res.data.data;
          this.setState({
            effectivePolicy: policyData,
          });
          if (policyData) {
            this.loadOwnershipTypes();
          }
        }
      })
      .catch(error => {
        handleApiError(error, t('api_retrieveDeviceDetailsError'), t);
      });
  };

  loadOwnershipTypes = () => {
    const { effectivePolicy } = this.state;
    this.setState({
      loadedGroups: effectivePolicy.deviceGroups.slice(0, loadLimit),
      loadedRoles: effectivePolicy.roles.slice(0, loadLimit),
      loadedUsers: effectivePolicy.users.slice(0, loadLimit),
    });
  };

  expandOwnershipTypes = () => {
    const { effectivePolicy, expandedOwnershipTypes } = this.state;
    if (!expandedOwnershipTypes) {
      this.setState(prevState => ({
        loadedGroups: effectivePolicy.deviceGroups,
        loadedRoles: effectivePolicy.roles,
        loadedUsers: effectivePolicy.users,
        expandedOwnershipTypes: !prevState.expandedOwnershipTypes,
      }));
    } else {
      this.setState(prevState => ({
        loadedGroups: effectivePolicy.deviceGroups.slice(0, loadLimit),
        loadedRoles: effectivePolicy.roles.slice(0, loadLimit),
        loadedUsers: effectivePolicy.users.slice(0, loadLimit),
        expandedOwnershipTypes: !prevState.expandedOwnershipTypes,
      }));
    }
  };

  render() {
    const {
      complianceData,
      effectivePolicy,
      loadedGroups,
      loadedRoles,
      loadedUsers,
      expandedOwnershipTypes,
    } = this.state;
    const { t } = this.props;
    if (effectivePolicy && complianceData) {
      return (
        <div>
          <Divider orientation="left">{t('label_effectivePolicy')}</Divider>
          <Card size="small" hoverable={true} className={styles.cardContainer}>
            <Row>
              <Col span={5}>
                {' '}
                <Row>
                  <Avatar
                    style={{
                      backgroundColor: complianceData.status
                        ? '#66BB6A'
                        : '#EF5350',
                      marginLeft: 10,
                    }}
                    size="large"
                    icon={
                      complianceData.status ? (
                        <CheckOutlined style={{ fontSize: 20 }} />
                      ) : (
                        <ExclamationOutlined style={{ fontSize: 20 }} />
                      )
                    }
                  />{' '}
                  <Title level={4} className={styles.title}>
                    {effectivePolicy.policyName.charAt(0).toUpperCase() +
                      effectivePolicy.policyName.slice(1)}
                  </Title>
                </Row>
              </Col>
              <Col span={2}>
                <LegacyIcon
                  type={effectivePolicy.profile.deviceType}
                  key="device-types"
                  theme={
                    this.config.defaultPlatformIcons[
                      effectivePolicy.profile.deviceType
                    ].theme
                  }
                  style={{
                    fontSize: 30,
                    color: this.config.defaultPlatformIcons[
                      effectivePolicy.profile.deviceType
                    ].color,
                    paddingTop: 5,
                  }}
                />
              </Col>
              <Col span={4}>
                <p className={styles.content}>
                  <Text strong>{t('label_complianceType')} :</Text>{' '}
                  {t(`label_${effectivePolicy.compliance.toLowerCase()}`)}
                </p>
              </Col>
              <Col span={4}>
                <p className={styles.content}>
                  <Text strong>{t('label_policyType')} :</Text>{' '}
                  {t(
                    `label_${effectivePolicy.policyType.toLowerCase()}_uppercase`,
                  )}
                </p>
              </Col>
              <Col span={7}>
                <Col span={7}>
                  <p className={styles.content}>
                    <Text strong>{t('label_ownershipType')} :</Text>
                  </p>
                </Col>
                {/* todo change this implementation to use a another View*/}
                <Row>
                  {effectivePolicy.deviceGroups.length > 0 ? (
                    <Space direction={'vertical'} align={'baseline'}>
                      {loadedGroups.map((group, index) => {
                        return (
                          <Tag color={'blue'} key={index}>
                            Group: {group.name}
                          </Tag>
                        );
                      })}
                    </Space>
                  ) : (
                    <></>
                  )}
                  {effectivePolicy.users.length > 0 ? (
                    <Space direction={'vertical'} align={'baseline'}>
                      {loadedUsers.map((user, index) => {
                        return (
                          <Tag color={'green'} key={index}>
                            User: {user}
                          </Tag>
                        );
                      })}
                    </Space>
                  ) : (
                    <Space direction={'vertical'} align={'baseline'}>
                      {loadedRoles.map((role, index) => {
                        return (
                          <Tag color={'green'} key={index}>
                            Role: {role}
                          </Tag>
                        );
                      })}
                    </Space>
                  )}
                </Row>
                {effectivePolicy.deviceGroups.length > loadLimit ||
                effectivePolicy.roles.length > loadLimit ||
                effectivePolicy.users.length > loadLimit ? (
                  <Row justify={'center'}>
                    <Button
                      style={{ borderColor: 'transparent' }}
                      block
                      onClick={this.expandOwnershipTypes}
                      icon={
                        <div>
                          {expandedOwnershipTypes ? (
                            <UpOutlined />
                          ) : (
                            <DownOutlined />
                          )}
                        </div>
                      }
                    />
                  </Row>
                ) : (
                  <></>
                )}
              </Col>
              <Col
                span={2}
                pull={effectivePolicy.deviceGroups.length === 0 ? 3 : 2}
              >
                <p className={styles.content}>
                  <Link
                    to={`/${this.config.appName}/policy/view/${effectivePolicy.profileId}`}
                  >
                    <EyeOutlined /> {t('label_viewPolicy')}
                  </Link>
                </p>
              </Col>
            </Row>
          </Card>
          {complianceData.complianceFeatures.length > 0 && (
            <div>
              <Divider orientation="left">
                {t('label_complianceFeatures')}
              </Divider>
              <Card
                size="small"
                hoverable={true}
                className={styles.cardContainer}
              >
                <List
                  itemLayout="horizontal"
                  dataSource={complianceData.complianceFeatures}
                  renderItem={item => (
                    <List.Item>
                      <List.Item.Meta
                        title={item.featureCode}
                        description={item.compliance}
                      />
                    </List.Item>
                  )}
                />
              </Card>
            </div>
          )}
        </div>
      );
    }
    return (
      <div>
        <Result
          status="404"
          title={t('noPolicy_Txt')}
          subTitle={t('createNewPolicy_Txt')}
          extra={
            <Link to={`/${this.config.appName}/policy/add`}>
              <Button type="primary">{t('label_createPolicy')}</Button>
            </Link>
          }
        />
      </div>
    );
  }
}

export default withConfigContext(withTranslation()(DevicePolicyCompliance));
